/**
 * The roadmap: the same picture as the project decks we already hand people.
 *
 * The treatment is the Gantt's: a colour for each phase carried down to the tasks under
 * it, a summary drawn as a bracket, a milestone as a diamond, and the filled part of a
 * bar for how far along it is. The two screens draw the same plan, so they draw it the
 * same way — this one is the read-only picture, laid out on the week-cell axis with
 * alternating bands and a TODAY line that the project decks use.
 *
 * Two panes that scroll as one: the names on the left, the timeline on the right. A
 * bar with no name beside it is decoration, and a name that scrolls away from its bar
 * is worse.
 *
 * The chart draws only what the data supports. An item with no dates has no bar; it is
 * listed underneath, where somebody can see how many there are and go and plan them. A
 * progress fill that cannot be derived is drawn as unknown, never as zero.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '../lib/router'
import clsx from 'clsx'
import { api } from '../lib/api'
import { TaskDrawer } from '../../components/TaskDrawer'
import { useProjectOptions } from '../lib/queries'
import { shortDate } from '../lib/format'
import { Button, EmptyState, Picker, Spinner } from '../components/ui'
import { PageHeader } from '../components/DataTable'
import { MarksLegend, MilestoneDiamond, SummaryBracket, TaskFill, phaseColours } from './schedule/marks'
import { DayHoursProvider, dayHours, formatLength, useDayHours } from './schedule/length'

type Bar = {
  id: string
  key: string
  title: string
  /** On the company timeline: which project the row belongs to, and whether it IS one. */
  project_id?: string
  is_project?: boolean
  type_key: string
  type_name: string
  depth: number
  parent_id: string | null
  has_children: boolean
  status_name: string
  status_category: string
  assignee: string | null
  progress_pct: number | null
  priority: string
  start: string
  end: string
  milestone: boolean
  dates_from: 'own' | 'children'
  late: boolean
  days: number
  /** Days that count as work. What the Gantt's Days column shows, and what a length means. */
  working_days?: number | null
}
type Loose = Omit<Bar, 'start' | 'end' | 'milestone' | 'dates_from' | 'late' | 'days'>
type Release = { id: string; name: string; on: string; released: boolean; late: boolean }
type Chart = {
  project: { id: string; key: string; name: string }
  today: string
  from: string
  to: string
  rows: Bar[]
  unscheduled: Loose[]
  releases: Release[]
  dependencies: { from: string; to: string }[]
  truncated: boolean
  /** What a working day is worth here, so a length reads in hours as well as days. */
  day_hours?: number
}

/* ------------------------------------------------------------------- geometry */

const ROW_H = 32
const BAR_H = 16
const NAME_W = 300

/**
 * The axis in lanes, so nothing has to be drawn on top of anything else.
 *
 * It used to be one 46px band with the period labels centred in it and TODAY dropped
 * over the top, which meant that for one month of the year — the one we are in — the
 * label underneath was unreadable. Marks that move get their own lane; the labels get
 * what is left.
 */
const TODAY_LANE = 14
const LABEL_LANE = 32
const RELEASE_LANE = 16
const axisHeight = (hasReleases: boolean) =>
  TODAY_LANE + LABEL_LANE + (hasReleases ? RELEASE_LANE : 0)

/** How many pixels a day gets. A year at day scale is a mile of scrolling. */
const ZOOM = [
  { value: 'quarter', label: 'Quarters', day: 1.4 },
  { value: 'month', label: 'Months', day: 3.6 },
  { value: 'week', label: 'Weeks', day: 11 },
  { value: 'day', label: 'Days', day: 26 },
]

const DAY = 86_400_000
const parse = (iso: string) => new Date(`${iso}T00:00:00`)
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY)
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY)

/**
 * A bar's colour: its phase's, unless it is late.
 *
 * Colouring by status made every phase look alike and said twice what the fill already
 * says. Late is the one thing that overrides the phase, and only on a task: a summary
 * is not late in its own right, the work under it is.
 */
function barColour(row: Bar, phase: string): string {
  if (row.late && !row.has_children) return 'var(--gantt-critical)'
  return phase
}

/** What is written beside a bar — the same line the Gantt writes. */
function barLabel(row: Bar, perDay: number | undefined): string {
  const done = row.progress_pct
  // Working days, not calendar days: the same task read "4d" here and "3d" on the Gantt
  // for as long as this counted weekends. And in hours too, as everywhere else — a
  // roadmap read in a budget meeting is read in hours.
  const parts = [formatLength(row.working_days ?? row.days, perDay)]
  if (done != null && done > 0) parts.push(`${Math.round(done)}%`)
  if (row.assignee) parts.push(row.assignee)
  return parts.join(' · ')
}

/** Saturday or Sunday, shaded so a bar's length is readable at a glance. */
const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6

/** Week starts inside the window: the axis cell of the decks. */
function weeks(from: Date, to: Date): Date[] {
  const out: Date[] = []
  const cursor = new Date(from)
  cursor.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7)) // back to Monday
  while (cursor <= to) {
    out.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 7)
  }
  return out
}

function months(from: Date, to: Date): Date[] {
  const out: Date[] = []
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1)
  while (cursor <= to) {
    out.push(new Date(cursor))
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return out
}

function quarters(from: Date, to: Date): Date[] {
  const out: Date[] = []
  const cursor = new Date(from.getFullYear(), Math.floor(from.getMonth() / 3) * 3, 1)
  while (cursor <= to) {
    out.push(new Date(cursor))
    cursor.setMonth(cursor.getMonth() + 3)
  }
  return out
}

/**
 * Where the chart comes from when it is not one project's plan — the company overview, which
 * draws every project of a company on one timeline, each a bracket over its own tasks.
 */
export type RoadmapSource = {
  /** The timeline payload's URL, without `include_closed`. */
  url: string
  /** The deck's URL for one of `auto`, `all` or `phases`. */
  deckUrl: (rows: string) => string
  title: string
  subtitle: string
}

export function RoadmapPage({ scopeTo, source }: { scopeTo?: string; source?: RoadmapSource } = {}) {
  const navigate = useNavigate()
  const projects = useProjectOptions()
  const [projectId, setProjectId] = useState<string | null>(null)
  const [zoom, setZoom] = useState('month')
  /**
   * CLOSED, not finished, and it moved with the rest of the plan.
   *
   * This asked for `include_done`, which took every COMPLETED task off the roadmap — including
   * the ones still sitting on the board. The endpoint now filters on `closed_at` instead, and
   * this follows it rather than being left pointing at a parameter that no longer exists and
   * silently doing nothing.
   */
  const [includeClosed, setIncludeClosed] = useState(true)

  const chosen = scopeTo ?? projectId ?? projects.data?.[0]?.value ?? null

  const chart = useQuery({
    queryKey: ['roadmap', source?.url ?? chosen, includeClosed],
    queryFn: () =>
      api.get<Chart>(
        source
          ? `${source.url}${source.url.includes('?') ? '&' : '?'}include_closed=${includeClosed}`
          : `/projects/${chosen}/timeline?include_closed=${includeClosed}`,
      ),
    enabled: Boolean(source || chosen),
  })
  const deckUrl = (rows: string) => (source ? source.deckUrl(rows) : `/projects/${chosen}/timeline.pptx?rows=${rows}`)
  /**
   * A TASK OPENS BESIDE THE ROADMAP, in the same drawer as on the board, so its dates can be put
   * right — or it can simply be read — without losing the chart. It used to navigate away to
   * the board's table. A project row on the company timeline still opens that project.
   */
  const [openTask, setOpenTask] = useState<{ id: string; projectId: string } | null>(null)
  const [downloadingDeck, setDownloadingDeck] = useState(false)
  const openRow = (row: { id: string; project_id?: string; is_project?: boolean }, fallbackProject: string) =>
    row.is_project
      ? navigate(`/projects/${row.id}`)
      : setOpenTask({ id: row.id, projectId: row.project_id ?? fallbackProject })

  const dayWidth = ZOOM.find((z) => z.value === zoom)?.day ?? 3.6

  const geom = useMemo(() => {
    if (!chart.data) return null
    const from = parse(chart.data.from)
    const to = parse(chart.data.to)
    const today = parse(chart.data.today)
    const width = Math.max(640, daysBetween(from, to) * dayWidth)
    const x = (iso: string) => daysBetween(from, parse(iso)) * dayWidth
    return { from, to, today, width, x, todayX: daysBetween(from, today) * dayWidth }
  }, [chart.data, dayWidth])

  const data = chart.data
  // The same rule the Gantt uses, over the same rows: a phase and everything under it
  // share a colour.
  const colours = useMemo(() => phaseColours(data?.rows ?? []), [data?.rows])
  // Releases ride the bottom of the axis. Reserving a lane for them when there are
  // none would be 16px of nothing above every chart.
  const axisH = axisHeight(Boolean(data?.releases.length))

  return (
    // The project's working day, so every length on the chart reads in hours as well.
    <DayHoursProvider value={dayHours(data?.day_hours)}>
    <div className="flex flex-col gap-5 fill-window">
      <PageHeader
        title={source?.title ?? 'Roadmap'}
        subtitle={source?.subtitle ?? 'What is planned, when, and how far along it is — from the dates and estimates already recorded.'}
        actions={
          <div className="flex items-center gap-2">
            {!scopeTo && !source && (
              <div className="w-52">
                <Picker
                  options={projects.data ?? []}
                  value={chosen}
                  onChange={setProjectId}
                  placeholder="Project"
                  clearable={false}
                />
              </div>
            )}
            <div className="w-28">
              <Picker
                options={ZOOM.map((z) => ({ value: z.value, label: z.label }))}
                value={zoom}
                onChange={(v) => setZoom(v ?? 'month')}
                clearable={false}
              />
            </div>
            <Button size="sm" onClick={() => setIncludeClosed((v) => !v)}>
              {includeClosed ? 'Hide closed' : 'Show closed'}
            </Button>
            {(chosen || source) && (
              // ONE DECK, ONE CLICK. This was a menu of three — every row, every row over as
              // many slides as it takes, or phases only — and the deck that gets used is the
              // second: one long timeline with every task on it. So that is what the button
              // downloads, without asking.
              <Button
                size="sm"
                variant="primary"
                disabled={downloadingDeck}
                onClick={() => {
                  setDownloadingDeck(true)
                  void api.download(deckUrl('all')).finally(() => setDownloadingDeck(false))
                }}
              >
                {downloadingDeck ? 'Building deck…' : 'Slide deck'}
              </Button>
            )}
          </div>
        }
      />

      {chart.isLoading || !geom || !data ? (
        <div className="grid place-items-center py-20"><Spinner /></div>
      ) : data.rows.length === 0 ? (
        <EmptyState
          title="Nothing on the roadmap yet"
          body="A work item needs a start and an end date to appear here. One date on its own becomes a milestone."
        />
      ) : (
        <div className="card overflow-hidden fill-column">
          <div className="flex overflow-x-auto fill-scroll">
            {/* Names. Sticky, so a bar always has its title beside it. */}
            <div
              className="sticky left-0 z-[3] shrink-0 border-r border-[var(--color-rule)] bg-[var(--color-surface)]"
              style={{ width: NAME_W }}
            >
              <div
                className="flex items-end border-b-2 border-[var(--gantt-axis-rule)] bg-[var(--gantt-axis-band)] px-3 pb-1.5 text-[11px] font-semibold tracking-wide text-[var(--color-ink-dim)] uppercase"
                style={{ height: axisH }}
              >
                {data.project.name}
              </div>
              {data.rows.map((row, i) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => openRow(row, data.project.id)}
                  className={clsx(
                    'flex w-full items-center gap-2 text-left hover:bg-[var(--color-surface-sunk)]',
                    i % 2 === 1 && 'bg-[var(--gantt-zebra)]',
                  )}
                  style={{ height: ROW_H, paddingLeft: 12 + row.depth * 14, paddingRight: 8 }}
                >
                  <span
                    className={clsx(
                      'truncate text-[12px]',
                      row.has_children && 'font-bold',
                      row.status_category === 'done' && 'text-[var(--color-ink-faint)]',
                    )}
                    title={`${row.key} · ${row.title}`}
                  >
                    {row.title}
                  </span>
                </button>
              ))}
            </div>

            {/* The timeline. */}
            <div className="relative shrink-0" style={{ width: geom.width }}>
              <Axis
                from={geom.from}
                to={geom.to}
                width={geom.width}
                height={axisH}
                dayWidth={dayWidth}
                releases={data.releases}
                x={geom.x}
              />

              <div className="relative">
                <Zebra
                  rows={data.rows.length}
                  from={geom.from}
                  days={daysBetween(geom.from, geom.to) + 1}
                  dayWidth={dayWidth}
                />
                <Dependencies
                  rows={data.rows}
                  links={data.dependencies}
                  x={geom.x}
                  width={geom.width}
                />

                {data.rows.map((row) => (
                  <div
                    key={row.id}
                    className="relative cursor-pointer"
                    style={{ height: ROW_H }}
                    onClick={() => openRow(row, data.project.id)}
                    title={row.is_project ? `Open ${row.title}` : `Open ${row.key} — ${row.title}`}
                  >
                    {row.milestone ? (
                      <Milestone row={row} left={geom.x(row.start)} />
                    ) : (
                      <TaskBar
                        row={row}
                        colour={barColour(row, colours.get(row.key) ?? 'var(--gantt-epic)')}
                        left={geom.x(row.start)}
                        right={geom.x(row.end) + dayWidth}
                      />
                    )}
                  </div>
                ))}
              </div>

              <TodayMarker
                left={geom.todayX}
                height={axisH + data.rows.length * ROW_H}
              />
            </div>
          </div>

          <Legend grouping={source ? 'project' : 'phase'} />
        </div>
      )}

      {data && data.unscheduled.length > 0 && (
        <section className="card p-4">
          <h2 className="text-[13px] font-semibold">
            Not on the roadmap ({data.unscheduled.length})
          </h2>
          <p className="mt-0.5 mb-3 text-[12px] text-[var(--color-ink-faint)]">
            These have no dates, so the chart has nothing to draw. Giving one a start and an
            end puts it on the roadmap; a single date makes it a milestone.
          </p>
          <ul className="flex flex-wrap gap-2">
            {data.unscheduled.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => openRow(row, data.project.id)}
                  className="flex items-center gap-2 rounded-[var(--radius-control)] border border-[var(--color-rule)] px-2 py-1 text-[12px] hover:bg-[var(--color-surface-sunk)]"
                >
                  <span className="font-mono text-[11px] text-[var(--color-accent-ink)]">
                    {row.key}
                  </span>
                  <span className="max-w-[24ch] truncate">{row.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {openTask && (
        <TaskDrawer
          itemId={openTask.id}
          projectId={openTask.projectId}
          onClose={() => setOpenTask(null)}
          // A changed date moves the bar, so the chart is read again behind the drawer.
          onChanged={() => void chart.refetch()}
        />
      )}
    </div>
    </DayHoursProvider>
  )
}

/* ----------------------------------------------------------------------- axis */

/**
 * Alternating cells rather than gridlines, which is what the decks do: a printed
 * timeline separates its periods by shading them, not by drawing a line at every one.
 */
function Axis({
  from, to, width, height, dayWidth, releases, x,
}: {
  from: Date
  to: Date
  width: number
  height: number
  dayWidth: number
  releases: Release[]
  x: (iso: string) => number
}) {
  // Band the whole drawn surface, not just as far as the last dated item. The chart has
  // a minimum width, so on a short project the periods used to stop halfway across and
  // leave a bare strip that looked like a rendering fault.
  const end = to > addDays(from, width / dayWidth) ? to : addDays(from, width / dayWidth)
  const cells = dayWidth >= 8 ? weeks(from, end) : dayWidth >= 3 ? months(from, end) : quarters(from, end)

  // Below this a cell cannot hold its own name, and what it renders instead is half a
  // letter — which is what the trailing part-month at the end of a chart was showing.
  const fits = dayWidth >= 8 ? 42 : dayWidth >= 3 ? 26 : 18

  const label = (d: Date, i: number): [string, string] => {
    if (dayWidth >= 8) {
      const end = addDays(d, 6)
      return [
        d.toLocaleDateString(undefined, { month: 'short' }) + ` W${Math.ceil(d.getDate() / 7)}`,
        `${d.getDate()}–${end.getDate()}`,
      ]
    }
    if (dayWidth >= 3) {
      return [d.toLocaleDateString(undefined, { month: 'short' }), i === 0 || d.getMonth() === 0 ? String(d.getFullYear()) : '']
    }
    return [`Q${Math.floor(d.getMonth() / 3) + 1}`, String(d.getFullYear())]
  }

  return (
    <div
      className="relative border-b-2 border-[var(--gantt-axis-rule)] bg-[var(--color-surface)]"
      style={{ height, width }}
    >
      {cells.map((start, i) => {
        const next = cells[i + 1] ?? end
        const left = daysBetween(from, start) * dayWidth
        const cellWidth = Math.min(daysBetween(start, next) * dayWidth, width - left)
        if (cellWidth <= 0) return null
        const [top, bottom] = label(start, i)
        return (
          <div
            key={start.toISOString()}
            className={clsx(
              'absolute top-0 flex flex-col items-center justify-center overflow-hidden',
              i % 2 === 0 && 'bg-[var(--gantt-axis-band)]',
            )}
            style={{
              left,
              width: cellWidth,
              height,
              // The labels sit in their own lane, clear of TODAY above them and of any
              // release riding the rule below.
              paddingTop: TODAY_LANE,
              paddingBottom: height - TODAY_LANE - LABEL_LANE,
            }}
          >
            {cellWidth >= fits && (
              <>
                <span className="text-[10px] font-semibold whitespace-nowrap text-[var(--color-ink-dim)]">
                  {top}
                </span>
                {bottom && (
                  <span className="text-[9px] whitespace-nowrap text-[var(--color-ink-faint)]">
                    {bottom}
                  </span>
                )}
              </>
            )}
          </div>
        )
      })}

      {/* Releases ride the axis, because that is where somebody looks for a date. */}
      {releases.map((r) => (
        <div
          key={r.id}
          className="absolute bottom-0 z-[2] flex translate-x-[-50%] items-center gap-1"
          style={{ left: x(r.on) }}
          title={`${r.name} · ${shortDate(r.on)}`}
        >
          <span
            aria-hidden
            className="size-2.5 rounded-full ring-2 ring-[var(--color-surface)]"
            style={{
              background: r.released
                ? 'var(--gantt-done)'
                : r.late
                  ? 'var(--gantt-critical)'
                  : 'var(--gantt-milestone)',
            }}
          />
          <span className="text-[10px] font-semibold whitespace-nowrap">{r.name}</span>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------- row background */

function Zebra({
  rows,
  from,
  days,
  dayWidth,
}: {
  rows: number
  from: Date
  days: number
  dayWidth: number
}) {
  // Weekends, once a day is wide enough for the shading to read as a day rather than as
  // a smudge. The Gantt shades them for the same reason: it is what makes a five-day bar
  // look like a week's work.
  const weekend =
    dayWidth >= 6
      ? Array.from({ length: days }, (_, d) => d).filter((d) => isWeekend(addDays(from, d)))
      : []
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {Array.from({ length: rows }, (_, i) =>
        i % 2 === 1 ? (
          <div
            key={i}
            className="absolute inset-x-0 bg-[var(--gantt-zebra)]"
            style={{ top: i * ROW_H, height: ROW_H }}
          />
        ) : null,
      )}
      {weekend.map((d) => (
        <div
          key={`w${d}`}
          className="absolute top-0 bg-[var(--color-ink)]/[0.05]"
          style={{ left: d * dayWidth, width: dayWidth, height: rows * ROW_H }}
        />
      ))}
    </div>
  )
}

/**
 * Where we are: one mark, drawn once.
 *
 * It was three things before — a word in the axis, a gap where the axis rule ran, and a
 * dashed line down the rows — and it read as three things. This is a sibling of both the
 * axis and the rows so it can run the full height of the chart uninterrupted.
 *
 * Solid, not dashed. A dashed border already means something on this chart — a bar whose
 * dates came from its children rather than from itself — and the legend says so.
 */
function TodayMarker({ left, height }: { left: number; height: number }) {
  // Centred on the line, except when the line is close enough to the left edge that
  // centring would push half the word out of the scroll container and leave it reading
  // "ODAY".
  const centred = left >= 26
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-0 z-[2]"
      style={{ left, height }}
    >
      <span
        className="absolute top-0 block text-[9px] leading-[12px] font-bold tracking-wider whitespace-nowrap uppercase"
        style={{
          color: 'var(--gantt-today)',
          left: centred ? 0 : 1,
          transform: centred ? 'translateX(-50%)' : 'none',
        }}
      >
        Today
      </span>
      <div
        className="absolute w-0 border-l-2"
        style={{
          top: TODAY_LANE,
          height: height - TODAY_LANE,
          borderColor: 'var(--gantt-today)',
        }}
      />
    </div>
  )
}

/* ----------------------------------------------------------------------- bars */

function TaskBar({
  row,
  colour,
  left,
  right,
}: {
  row: Bar
  colour: string
  left: number
  right: number
}) {
  const perDay = useDayHours()
  const width = Math.max(4, right - left)
  const done = row.progress_pct ?? 0

  return (
    <div
      className="absolute z-[2] flex items-center"
      style={{ left, top: (ROW_H - BAR_H) / 2, height: BAR_H }}
      title={`${row.key} · ${row.title}
${shortDate(row.start)} to ${shortDate(row.end)} · ${formatLength(row.working_days ?? row.days, perDay)}
${row.status_name}${row.progress_pct == null ? '' : ` · ${Math.round(row.progress_pct)}% complete`}${row.late ? ' · late' : ''}`}
    >
      <div className="relative h-full" style={{ width }}>
        {row.has_children ? (
          <SummaryBracket colour={colour} />
        ) : (
          <TaskFill colour={colour} done={done} hollow={row.dates_from !== 'own'} />
        )}
      </div>
      <span className="ml-1.5 shrink-0 text-[10px] whitespace-nowrap text-[var(--color-ink-dim)]">
        {row.has_children ? (
          <span className="font-semibold" style={{ color: colour }}>
            {row.title}
            {row.progress_pct != null && row.progress_pct > 0 ? ` · ${Math.round(row.progress_pct)}%` : ''}
          </span>
        ) : (
          barLabel(row, perDay)
        )}
      </span>
    </div>
  )
}

function Milestone({ row, left }: { row: Bar; left: number }) {
  // One colour for a milestone, as on the Gantt. Whether it has happened is the row's
  // status, which is written beside it; a diamond that changes colour says the same
  // thing in a second language.
  const colour = 'var(--gantt-milestone)'
  return (
    <div
      className="absolute z-[2] flex translate-x-[-50%] items-center gap-1.5"
      style={{ left, top: (ROW_H - 14) / 2 }}
      title={`${row.key} · ${row.title}
${shortDate(row.start)} · ${row.status_name}${row.late ? ' · late' : ''}`}
    >
      {/* A diamond, the same mark the Gantt draws: a point in time, not a span. */}
      <MilestoneDiamond colour={colour} />
      <span className="translate-x-1 text-[9px] font-semibold whitespace-nowrap text-[var(--color-ink-faint)]">
        {shortDate(row.start)}
      </span>
    </div>
  )
}

/* --------------------------------------------------------------- dependencies */

/**
 * Elbow connectors from a blocker's end to its dependant's start.
 *
 * Only between rows the chart is showing, so a link to something filtered out cannot
 * leave an arrow pointing at nothing.
 */
/**
 * Which ends of two bars a dependency joins.
 *
 * The four a planner expects. Drawing them all finish-to-start, as this did, states
 * something untrue about three of them: work that must begin together is not work that
 * begins after the other ends.
 *
 * Exported so the geometry can be tested without a browser — it is arithmetic, and the
 * thing most likely to be quietly wrong.
 */
export type Relation = 'FS' | 'SS' | 'FF' | 'SF'

export function anchors(
  relation: Relation,
): { from: 'start' | 'end'; to: 'start' | 'end' } {
  switch (relation) {
    case 'SS':
      return { from: 'start', to: 'start' }
    case 'FF':
      return { from: 'end', to: 'end' }
    case 'SF':
      return { from: 'start', to: 'end' }
    default:
      return { from: 'end', to: 'start' }
  }
}

function Dependencies({
  rows, links, x, width,
}: {
  rows: Bar[]
  links: { from: string; to: string; relation?: Relation; lag_days?: number }[]
  x: (iso: string) => number
  width: number
}) {
  const index = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows])
  const paths = links.flatMap((link) => {
    const a = index.get(link.from)
    const b = index.get(link.to)
    if (a === undefined || b === undefined) return []
    const from = rows[a]!
    const to = rows[b]!
    const ends = anchors(link.relation ?? 'FS')
    const x1 = x(ends.from === 'end' ? from.end : from.start) + (ends.from === 'end' ? 4 : -4)
    const y1 = a * ROW_H + ROW_H / 2
    const x2 = x(ends.to === 'end' ? to.end : to.start) + (ends.to === 'end' ? 7 : -7)
    const y2 = b * ROW_H + ROW_H / 2
    // The elbow turns clear of whichever bar it leaves, in the direction it is going.
    const turn = x1 + (x2 >= x1 ? 8 : -8)
    const head = ends.to === 'end' ? -1 : 1
    const lag = link.lag_days ?? 0
    return [{
      key: `${link.from}-${link.to}-${link.relation ?? 'FS'}`,
      d: `M${x1},${y1} H${turn} V${y2} H${x2}`,
      x2,
      y2,
      head,
      lag,
      mid: (turn + x2) / 2,
    }]
  })

  if (paths.length === 0) return null
  return (
    <svg
      className="pointer-events-none absolute inset-0 z-[1]"
      width={width}
      height={rows.length * ROW_H}
      aria-hidden
    >
      {paths.map((p) => (
        <g key={p.key}>
          <path d={p.d} fill="none" stroke="var(--color-ink-faint)" strokeWidth="1" />
          {/* The arrowhead points into the bar it lands on, whichever side that is. */}
          <path
            d={`M${p.x2 + 5 * p.head},${p.y2} l${-5 * p.head},-3.5 v7 z`}
            fill="var(--color-ink-faint)"
          />
          {/* A wait or an overlap is part of the plan, so it is on the plan. */}
          {p.lag !== 0 && (
            <text
              x={p.mid}
              y={p.y2 - 4}
              textAnchor="middle"
              className="fill-[var(--color-ink-faint)] text-[9px]"
            >
              {p.lag > 0 ? `+${p.lag}d` : `${p.lag}d`}
            </text>
          )}
        </g>
      ))}
    </svg>
  )
}

/* --------------------------------------------------------------------- legend */

function Legend({ grouping }: { grouping: 'phase' | 'project' }) {
  return (
    <MarksLegend
      grouping={grouping}
      extra={[
        ['Late', 'var(--gantt-critical)'],
        ['Milestone', 'var(--gantt-milestone)'],
      ]}
      trailing={
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="inline-block h-2.5 w-4 rounded-[2px] border border-dashed border-[var(--color-ink-faint)]"
          />
          Dates from the work under it
        </span>
      }
    />
  )
}
